import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Info, Database, Trash2, ChevronRight, GitMerge, CalendarDays, Bell, UsersRound, Map as MapIcon, UserPlus, Home, Radar, Flame, Globe, Building2, Rocket, Activity, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAppData } from "@/context/AppDataContext";
import { UnreadBadge, useVisitorAlerts } from "@/components/VisitorAlerts";
import { exampleDataApi, apiErrorMessage } from "@/api/client";
import { HomeLocationDialog } from "@/components/forms/HomeLocationDialog";
import { InviteDialog } from "@/components/forms/InviteDialog";
import { brand } from "@/theme/themeTokens";
import { toast } from "sonner";

function Row({ testid, icon: Icon, label, sub, onClick, danger, badge }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-4 text-left last:border-0 hover:bg-slate-50">
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${danger ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold ${danger ? "text-rose-600" : "text-slate-800"}`}>{label}</span>
        {sub && <span className="block truncate text-xs text-slate-400">{sub}</span>}
      </span>
      {badge}
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );
}

export default function More() {
  const { user, logout, isAdmin } = useAuth();
  const isManager = ["sales_manager", "admin", "super_admin"].includes(user?.role);
  const { unread: visitorUnread } = useVisitorAlerts();
  const { meta } = useAppData();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [homeOpen, setHomeOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const exampleEnabled = meta?.example_data_enabled;

  const loadExample = async () => {
    setBusy(true);
    try {
      const { data } = await exampleDataApi.load();
      toast.success(`Loaded ${data.organisations_created} example organisations`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const unloadExample = async () => {
    setBusy(true);
    try {
      const { data } = await exampleDataApi.unload();
      toast.success(`Removed ${data.organisations_removed} example organisations`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:py-8">
      <h1 className="mb-4 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">More</h1>

      {/* Profile card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="more-profile">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-700 font-heading text-lg font-bold text-white">
            {(user?.name || "?").charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="truncate font-heading text-base font-bold text-slate-900">{user?.name}</p>
            <p className="truncate text-sm text-slate-500">{user?.email}</p>
            <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold capitalize text-slate-600">
              {user?.role?.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      </div>

      {/* Everyone: my workspace */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Row testid="more-availability-link" icon={CalendarDays} label="My availability"
          sub="Field, phone and email/desk time" onClick={() => navigate("/availability")} />
        <Row testid="more-notifications-link" icon={Bell} label="Notifications"
          sub="New CaterCTRL prospects & alerts" onClick={() => navigate("/notifications")} />
        <Row testid="more-notification-prefs-link" icon={Bell} label="Notification preferences"
          sub="Areas, priority and rating filters" onClick={() => navigate("/notification-preferences")} />
        <Row testid="more-home-location-link" icon={Home} label="Home base"
          sub="Private — only you can see this" onClick={() => setHomeOpen(true)} />
      </div>

      {/* Manager+: team & delegation */}
      {isManager && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Row testid="more-team-link" icon={UsersRound} label="Team"
            sub="Reps, workload and availability" onClick={() => navigate("/team")} />
          <Row testid="more-territories-link" icon={MapIcon} label="Territories"
            sub="Coverage areas for allocation" onClick={() => navigate("/territories")} />
          <Row testid="more-monitored-areas-link" icon={Radar} label="Monitored areas"
            sub="Automatic FSA scan health & status" onClick={() => navigate("/monitored-areas")} />
          <Row testid="more-hotspots-link" icon={Flame} label="Hotspots"
            sub="Demand & cold-prospect clusters" onClick={() => navigate("/hotspots")} />
          <Row testid="more-visitors-link" icon={Globe} label="Website visitors & chat"
            sub="Anonymous visitor activity and conversations" onClick={() => navigate("/visitors")}
            badge={<UnreadBadge count={visitorUnread} testid="more-visitors-unread-badge" />} />
          <Row testid="more-invite-link" icon={UserPlus} label="Invite user"
            sub="Send an invitation link" onClick={() => setInviteOpen(true)} />
        </div>
      )}

      {/* Admin: users + audit */}
      {isAdmin && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Row testid="more-users-link" icon={Users} label="Users"
            sub="Manage team access and roles" onClick={() => navigate("/users")} />
          <Row testid="more-website-intent-link" icon={Globe} label="Website Intent"
            sub="Product-site ingestion health & install" onClick={() => navigate("/admin/website-intent")} />
          <Row testid="more-website-kits-link" icon={Download} label="Website kits"
            sub="Download brand website integration kits" onClick={() => navigate("/admin/website-kits")} />
          <Row testid="more-diagnostics-link" icon={Activity} label="Web Intent Diagnostics"
            sub="Read-only inbound lead pipeline evidence" onClick={() => navigate("/admin/diagnostics/webintent")} />
          <Row testid="more-ch-reviews-link" icon={Building2} label="Companies House Reviews"
            sub="Approve or reject uncertain company matches" onClick={() => navigate("/admin/companies-house")} />
          <Row testid="more-pilot-link" icon={Rocket} label="Pilot Dashboard"
            sub="Providers, retention, activity & diagnostics" onClick={() => navigate("/admin/pilot")} />
          <Row testid="more-audit-link" icon={Info} label="Audit trail"
            sub="Immutable record of tenant activity" onClick={() => navigate("/audit")} />
        </div>
      )}

      {/* Manager+: duplicate review queue */}
      {isManager && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Row testid="more-reviews-link" icon={GitMerge} label="Duplicate review queue"
            sub="Resolve possible prospect matches" onClick={() => navigate("/reviews")} />
        </div>
      )}

      {/* Example data (non-production only) */}
      {exampleEnabled && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Development tools · {meta?.env}
          </div>
          <Row testid="more-load-example-data-button" icon={Database} label="Load example data"
            sub="Inserts clearly-labelled [EXAMPLE] records" onClick={busy ? undefined : loadExample} />
          <Row testid="more-unload-example-data-button" icon={Trash2} label="Unload example data"
            sub="Removes only [EXAMPLE] records" onClick={busy ? undefined : unloadExample} danger />
        </div>
      )}

      {/* About */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 py-4">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Info className="h-4.5 w-4.5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800" data-testid="about-app">About {brand.name}</p>
            <p className="text-xs text-slate-400">Version {brand.version} · {brand.phase}</p>
          </div>
        </div>
      </div>

      {/* Logout */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Row testid="more-logout-button" icon={LogOut} label="Log out" onClick={logout} danger />
      </div>

      <HomeLocationDialog open={homeOpen} onOpenChange={setHomeOpen} />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
